import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { io } from "socket.io-client";
import { API_URL, SOCKET_URL } from "../config/apiConfig";
import "./LiveTracking.css";

/* ─── SVG icon helpers ─────────────────────────────────────────────────────── */
const CarIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
    <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
  </svg>
);

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
  </svg>
);

const LocationPinIcon = ({ color = "#6366f1" }) => (
  <svg viewBox="0 0 24 24" fill={color} width="18" height="18">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
  </svg>
);

const FlagIcon = () => (
  <svg viewBox="0 0 24 24" fill="#ef4444" width="18" height="18">
    <path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z"/>
  </svg>
);

/* ─── Map marker SVG data URIs ─────────────────────────────────────────────── */
const makeMarkerSVG = (color, emoji = "") =>
  `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="50" viewBox="0 0 40 50">
      <ellipse cx="20" cy="47" rx="8" ry="3" fill="rgba(0,0,0,0.2)"/>
      <path d="M20 0C11.16 0 4 7.16 4 16c0 12 16 34 16 34S36 28 36 16C36 7.16 28.84 0 20 0z" fill="${color}"/>
      <circle cx="20" cy="16" r="10" fill="white" fill-opacity="0.9"/>
      <text x="20" y="21" text-anchor="middle" font-size="13">${emoji}</text>
    </svg>`
  )}`;

const PICKUP_ICON_URL    = makeMarkerSVG("#10b981", "🏠");
const DROP_ICON_URL      = makeMarkerSVG("#ef4444", "🏁");
const STOP_ICON_URL      = makeMarkerSVG("#f59e0b", "📍");
const CAR_ICON_URL       = makeMarkerSVG("#6366f1", "🚗");
const PASSENGER_ICON_URL = makeMarkerSVG("#f97316", "🧑");

/* ── Clean letter-based markers for Pickup (P) and Drop (D) ─────────────── */
const makeLetterMarkerSVG = (color, letter) =>
  `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="46" viewBox="0 0 36 46">
      <ellipse cx="18" cy="44" rx="7" ry="2.5" fill="rgba(0,0,0,0.18)"/>
      <path d="M18 0C10.27 0 4 6.27 4 14c0 10.5 14 30 14 30S32 24.5 32 14C32 6.27 25.73 0 18 0z" fill="${color}"/>
      <circle cx="18" cy="14" r="10" fill="white"/>
      <text x="18" y="19" text-anchor="middle" font-size="14" font-weight="bold" font-family="Arial,sans-serif" fill="${color}">${letter}</text>
    </svg>`
  )}`;

const PICKUP_LETTER_ICON = makeLetterMarkerSVG("#10b981", "P");
const DROP_LETTER_ICON   = makeLetterMarkerSVG("#ef4444", "D");

/* ─── Smooth position interpolation ───────────────────────────────────────── */
const lerp = (a, b, t) => a + (b - a) * t;

function smoothMoveMarker(marker, targetLat, targetLng, steps = 25) {
  const startPos = marker.getPosition();
  const startLat = startPos.lat();
  const startLng = startPos.lng();
  let step = 0;

  const animate = () => {
    step++;
    const t = step / steps;
    const easedT = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; // ease-in-out
    const lat = lerp(startLat, targetLat, easedT);
    const lng = lerp(startLng, targetLng, easedT);
    marker.setPosition(new window.google.maps.LatLng(lat, lng));
    if (step < steps) requestAnimationFrame(animate);
  };
  requestAnimationFrame(animate);
}

/* ─── Component ────────────────────────────────────────────────────────────── */
export default function LiveTracking() {
  const { rideId } = useParams();

  const mapRef           = useRef(null);   // DOM node
  const googleMap        = useRef(null);   // google.maps.Map instance
  const liveMarker       = useRef(null);   // driver live-location marker 🚗
  const passengerMarker  = useRef(null);   // passenger live-location marker 🧑
  const socketRef        = useRef(null);
  const mapLoaded        = useRef(false);

  const [ride, setRide]                       = useState(null);
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState(null);
  const [socketStatus, setSocketStatus]       = useState("connecting");
  const [lastUpdate, setLastUpdate]           = useState(null);
  const [liveCoords, setLiveCoords]           = useState(null);       // driver coords
  const [passengerCoords, setPassengerCoords] = useState(null);       // passenger coords
  const [walletOtpData, setWalletOtpData]     = useState(null);       // wallet payment OTP

  /* ── 1. Load ride details ─────────────────────────────────────────────── */
  useEffect(() => {
    if (!rideId) return;
    (async () => {
      try {
        const res  = await fetch(`${API_URL}/live-tracking/${rideId}`);
        const json = await res.json();
        if (!json.success) throw new Error(json.message || "Ride not found");
        setRide(json.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [rideId]);

  /* ── 2. Initialise Google Maps once the ride data is ready ───────────── */
  const initMap = useCallback(() => {
    if (!mapRef.current || !window.google || mapLoaded.current) return;
    mapLoaded.current = true;

    const center = { lat: 28.6139, lng: 77.209 }; // default: New Delhi

    const map = new window.google.maps.Map(mapRef.current, {
      zoom: 13,
      center,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true,
      styles: [
        { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
        { featureType: "transit", elementType: "labels", stylers: [{ visibility: "off" }] },
      ],
    });

    googleMap.current = map;

    const bounds = new window.google.maps.LatLngBounds();
    let hasCoord = false;

    const addMarker = (lat, lng, iconUrl, title) => {
      if (lat == null || lng == null) return null;
      const pos = { lat: parseFloat(lat), lng: parseFloat(lng) };
      bounds.extend(pos);
      hasCoord = true;
      return new window.google.maps.Marker({
        position: pos,
        map,
        title,
        icon: { url: iconUrl, scaledSize: new window.google.maps.Size(40, 50) },
      });
    };

    // Geocode an address when lat/lng are missing, then place the marker
    const geocodeAndAddMarker = (address, iconUrl, title) => {
      if (!address) return;
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        if (status === "OK" && results[0]) {
          const loc = results[0].geometry.location;
          const marker = new window.google.maps.Marker({
            position: loc,
            map,
            title,
            icon: { url: iconUrl, scaledSize: new window.google.maps.Size(40, 50) },
          });
          bounds.extend(loc);
          map.fitBounds(bounds, { padding: 60 });
          console.log(`[LiveTracking] Geocoded "${address}" → ${loc.lat()},${loc.lng()}`);
        } else {
          console.warn(`[LiveTracking] Geocoding failed for "${address}":`, status);
        }
      });
    };

    if (ride) {
      // Pickup marker — use coords if available, otherwise geocode
      if (ride.pickup.lat != null && ride.pickup.lng != null) {
        addMarker(ride.pickup.lat, ride.pickup.lng, PICKUP_LETTER_ICON, "Pickup (P)");
      } else if (ride.pickup.address) {
        geocodeAndAddMarker(ride.pickup.address, PICKUP_LETTER_ICON, "Pickup (P)");
      }

      // Drop marker — use coords if available, otherwise geocode
      if (ride.drop.lat != null && ride.drop.lng != null) {
        addMarker(ride.drop.lat, ride.drop.lng, DROP_LETTER_ICON, "Drop (D)");
      } else if (ride.drop.address) {
        geocodeAndAddMarker(ride.drop.address, DROP_LETTER_ICON, "Drop (D)");
      }

      // Stops
      (ride.stops || []).forEach((s, i) =>
        addMarker(s.lat, s.lng, STOP_ICON_URL, `Stop ${i + 1}`)
      );

      // DON'T create live marker at a default position —
      // it will be created when the first real location update arrives
    }

    if (hasCoord) map.fitBounds(bounds, { padding: 60 });
  }, [ride]);

  useEffect(() => {
    if (!ride || loading) return;

    const loadGoogleMaps = async () => {
      // ── Case 1: google not loaded at all → inject stable script ────────────
      if (!window.google) {
        if (!document.getElementById("google-maps-live-track")) {
          const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
          // Use a named callback so we know exactly when Maps is ready
          window.__initLiveTrackMap = initMap;
          const script = document.createElement("script");
          script.id  = "google-maps-live-track";
          // v=weekly = stable channel (no beta constructor issues)
          script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&v=weekly&libraries=places&callback=__initLiveTrackMap`;
          script.async = true;
          document.head.appendChild(script);
        }
        return;
      }

      // ── Case 2: New/beta API loaded by App.js → use importLibrary ──────────
      if (typeof window.google.maps?.importLibrary === "function") {
        try {
          await window.google.maps.importLibrary("maps");
          await window.google.maps.importLibrary("marker");
          initMap();
        } catch (e) {
          console.error("[LiveTracking] importLibrary failed:", e);
        }
        return;
      }

      // ── Case 3: Classic stable API already present ───────────────────────
      if (typeof window.google.maps?.Map === "function") {
        initMap();
        return;
      }

      // ── Case 4: google object exists but Maps not ready yet — poll ────────
      let attempts = 0;
      const waitForMaps = setInterval(() => {
        attempts++;
        if (typeof window.google?.maps?.Map === "function") {
          clearInterval(waitForMaps);
          initMap();
        } else if (attempts > 50) {
          clearInterval(waitForMaps);
          console.error("[LiveTracking] Google Maps did not become ready in time.");
        }
      }, 200);
    };

    loadGoogleMaps();
  }, [ride, loading, initMap]);


  /* ── 3. Socket.IO connection + reconnect logic ────────────────────────── */
  useEffect(() => {
    if (!rideId) return;

    // 🔑 CRITICAL: Always connect to PRODUCTION socket for live tracking.
    // The driver mobile app ALWAYS sends location to production (backend.cabzigo.com),
    // regardless of whether this page is on localhost or production.
    // Using a local socket (localhost:5000) would never receive driver updates.
    const LIVE_TRACK_SOCKET_URL = "https://backend.cabzigo.com";

    const socket = io(LIVE_TRACK_SOCKET_URL, {
      // ── Force polling first: WebSocket through NGINX needs proxy_set_header Upgrade ──
      // Once NGINX is configured for WS, this auto-upgrades
      transports: ["polling", "websocket"],
      reconnectionAttempts: Infinity,   // never give up reconnecting
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 30000,
    });

    socketRef.current = socket;

    const joinRoom = () => {
      socket.emit("joinRideRoom", rideId);
      console.log("[LiveTracking] Emitted joinRideRoom for:", rideId);
    };

    // fires on initial connect AND every reconnect in Socket.IO v4
    socket.on("connect", () => {
      console.log("[LiveTracking] ✅ Socket connected:", socket.id, "| transport:", socket.io.engine.transport.name);
      setSocketStatus("live");
      joinRoom();
    });

    // Also rejoin on Manager-level reconnect (belt + suspenders)
    socket.io.on("reconnect", () => {
      console.log("[LiveTracking] 🔄 Manager reconnected — rejoining room");
      setSocketStatus("live");
      joinRoom();
    });

    socket.on("rideRoomJoined", (data) => {
      console.log("[LiveTracking] Joined ride room:", data.room);
    });

    socket.on("wallet_payment_otp", (data) => {
      const cleanId = (rideId || "").toString().trim().toLowerCase();
      const cleanBookingId = (data.bookingId || "").toString().trim().toLowerCase();
      const cleanBookingNumber = (data.bookingNumber || "").toString().trim().toLowerCase();

      if (cleanBookingId === cleanId || cleanBookingNumber === cleanId) {
        console.log("[LiveTracking] Received wallet payment OTP:", data);
        setWalletOtpData(data);
      }
    });

    socket.on("locationUpdate", ({ lat, lng, type }) => {
      const parsedLat = parseFloat(lat);
      const parsedLng = parseFloat(lng);
      console.log(`[LiveTracking] 📍 locationUpdate [${type || 'driver'}]:`, parsedLat, parsedLng);
      setLastUpdate(new Date());

      if (!type || type === 'driver') {
        // ── Driver marker (car) ──
        setLiveCoords({ lat: parsedLat, lng: parsedLng });
        if (liveMarker.current) {
          smoothMoveMarker(liveMarker.current, parsedLat, parsedLng);
        } else if (googleMap.current) {
          const pos = { lat: parsedLat, lng: parsedLng };
          liveMarker.current = new window.google.maps.Marker({
            position: pos,
            map: googleMap.current,
            title: "Driver Location",
            icon: { url: CAR_ICON_URL, scaledSize: new window.google.maps.Size(40, 50) },
            zIndex: 999,
          });
        }
        // Auto-pan map to follow driver
        if (googleMap.current) googleMap.current.panTo({ lat: parsedLat, lng: parsedLng });
      } else if (type === 'passenger') {
        // ── Passenger marker (person) ──
        setPassengerCoords({ lat: parsedLat, lng: parsedLng });
        if (passengerMarker.current) {
          smoothMoveMarker(passengerMarker.current, parsedLat, parsedLng);
        } else if (googleMap.current) {
          const pos = { lat: parsedLat, lng: parsedLng };
          passengerMarker.current = new window.google.maps.Marker({
            position: pos,
            map: googleMap.current,
            title: "Passenger Location",
            icon: { url: PASSENGER_ICON_URL, scaledSize: new window.google.maps.Size(40, 50) },
            zIndex: 998,
          });
        }
        // Auto-pan map to follow passenger
        if (googleMap.current) googleMap.current.panTo({ lat: parsedLat, lng: parsedLng });
      }
    });

    socket.on("disconnect", (reason) => {
      console.warn("[LiveTracking] Socket disconnected:", reason);
      setSocketStatus("disconnected");
    });

    socket.on("connect_error", (err) => {
      console.error("[LiveTracking] Socket connect error:", err.message);
      setSocketStatus("disconnected");
    });

    socket.on("reconnect", () => {
      setSocketStatus("live");
      socket.emit("joinRideRoom", rideId);
    });

    return () => {
      socket.disconnect();
    };
  }, [rideId]);

  /* ── 4. HTTP polling fallback — survives socket drops ──────────────────── */
  useEffect(() => {
    if (!rideId) return;

    const pollPositions = async () => {
      try {
        // Always poll from production — driver location is cached there
        const PROD_API = "https://backend.cabzigo.com/api";
        const res = await fetch(`${PROD_API}/live-tracking/${rideId}/positions`);
        const json = await res.json();
        if (!json.success || !Array.isArray(json.data)) return;

        json.data.forEach(({ type, lat, lng }) => {
          const pLat = parseFloat(lat);
          const pLng = parseFloat(lng);
          if (isNaN(pLat) || isNaN(pLng)) return;

          if (!type || type === 'driver') {
            setLiveCoords({ lat: pLat, lng: pLng });
            setLastUpdate(new Date());
            if (liveMarker.current) {
              smoothMoveMarker(liveMarker.current, pLat, pLng);
            } else if (googleMap.current) {
              liveMarker.current = new window.google.maps.Marker({
                position: { lat: pLat, lng: pLng },
                map: googleMap.current,
                title: "Driver Location",
                icon: { url: CAR_ICON_URL, scaledSize: new window.google.maps.Size(40, 50) },
                zIndex: 999,
              });
            }
            // Auto-pan map to follow driver
            if (googleMap.current) googleMap.current.panTo({ lat: pLat, lng: pLng });
          } else if (type === 'passenger') {
            setPassengerCoords({ lat: pLat, lng: pLng });
            setLastUpdate(new Date());
            if (passengerMarker.current) {
              smoothMoveMarker(passengerMarker.current, pLat, pLng);
            } else if (googleMap.current) {
              passengerMarker.current = new window.google.maps.Marker({
                position: { lat: pLat, lng: pLng },
                map: googleMap.current,
                title: "Passenger Location",
                icon: { url: PASSENGER_ICON_URL, scaledSize: new window.google.maps.Size(40, 50) },
                zIndex: 998,
              });
            }
            // Auto-pan map to follow passenger
            if (googleMap.current) googleMap.current.panTo({ lat: pLat, lng: pLng });
          }
        });
      } catch (err) {
        // Silently ignore — socket may still be working
      }
    };

    // Poll every 8 seconds as a backup
    const interval = setInterval(pollPositions, 8000);
    // Also run once immediately
    pollPositions();

    return () => clearInterval(interval);
  }, [rideId]);

  /* ── Helpers ──────────────────────────────────────────────────────────── */
  const formatTime = (date) => {
    if (!date) return "--";
    return date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  };

  const statusColor = {
    pending:   "#f59e0b",
    confirmed: "#3b82f6",
    assigned:  "#8b5cf6",
    started:   "#10b981",
    completed: "#6b7280",
    cancelled: "#ef4444",
  };

  /* ── Render ───────────────────────────────────────────────────────────── */
  if (loading) return <TrackingLoader />;
  if (error)   return <TrackingError message={error} rideId={rideId} />;

  /* ── Show "Ride Completed / Cancelled" screen when link is no longer active ── */
  if (ride.status === 'completed' || ride.status === 'cancelled') {
    const isCompleted = ride.status === 'completed';
    return (
      <div className="lt-root" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#0f172a' }}>
        <div style={{
          textAlign: 'center', padding: 48, borderRadius: 24,
          background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.1)', maxWidth: 440,
        }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>{isCompleted ? '✅' : '❌'}</div>
          <h1 style={{ color: '#fff', fontSize: 26, fontWeight: 700, marginBottom: 8 }}>
            Ride {isCompleted ? 'Completed' : 'Cancelled'}
          </h1>
          <p style={{ color: '#94a3b8', fontSize: 15, marginBottom: 6 }}>
            Ride <strong style={{ color: '#e2e8f0' }}>#{ride.rideId}</strong>
          </p>
          <p style={{ color: '#64748b', fontSize: 14, marginBottom: 24 }}>
            {isCompleted
              ? 'This ride has been completed successfully. Live tracking is no longer available.'
              : 'This ride has been cancelled. Live tracking is no longer available.'}
          </p>
          {ride.passenger && (
            <p style={{ color: '#94a3b8', fontSize: 14 }}>
              Passenger: <strong style={{ color: '#e2e8f0' }}>{ride.passenger.name}</strong>
            </p>
          )}
          {ride.driver && (
            <p style={{ color: '#94a3b8', fontSize: 14, marginTop: 4 }}>
              Driver: <strong style={{ color: '#e2e8f0' }}>{ride.driver.name}</strong>
            </p>
          )}
          <a href="/" style={{
            display: 'inline-block', marginTop: 28, padding: '10px 28px',
            background: 'linear-gradient(135deg,#6366f1,#4f46e5)', color: '#fff',
            borderRadius: 12, textDecoration: 'none', fontWeight: 600, fontSize: 14,
          }}>Go Home</a>
        </div>
      </div>
    );
  }

  return (
    <div className="lt-root">
      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <header className="lt-header">
        <div className="lt-header-brand">
          <span className="lt-brand-icon">🚖</span>
          <span className="lt-brand-name">CabZigo Live</span>
        </div>
        <div className="lt-header-ride-id">
          Ride&nbsp;<strong>#{ride.rideId}</strong>
        </div>
        <div className={`lt-socket-badge lt-socket-${socketStatus}`}>
          <span className="lt-pulse-dot" />
          {socketStatus === "live" ? "Live" : socketStatus === "connecting" ? "Connecting…" : "Reconnecting…"}
        </div>
      </header>

      <div className="lt-body">
        {/* ── Google Map ────────────────────────────────────────────────── */}
        <div className="lt-map-wrap">
          <div ref={mapRef} className="lt-map" id="live-tracking-map" />

          {/* Floating status overlay */}
          <div className="lt-map-overlay">
            <div
              className="lt-status-pill"
              style={{ background: statusColor[ride.status] || "#6b7280" }}
            >
              {(ride.status || "unknown").toUpperCase()}
            </div>
            {lastUpdate && (
              <div className="lt-update-pill">
                Last update&nbsp;·&nbsp;{formatTime(lastUpdate)}
              </div>
            )}
          </div>
        </div>

        {/* ── Info panel ────────────────────────────────────────────────── */}
        <aside className="lt-panel">
          {/* Wallet OTP Banner if generated */}
          {walletOtpData && (
            <section className="lt-card lt-otp-card" style={{
              background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
              border: '2px solid #3b82f6',
              boxShadow: '0 4px 20px rgba(59,130,246,0.4)',
              color: 'white',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '16px'
            }}>
              <h2 className="lt-card-title" style={{ color: 'white', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '15px', fontWeight: 'bold', borderBottom: '1px solid rgba(255,255,255,0.15)', paddingBottom: '8px', marginBottom: '12px' }}>
                🔒 Wallet Payment OTP
              </h2>
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: '12px', color: '#bfdbfe', marginBottom: '8px' }}>Share this OTP with the driver to authorize payment from your wallet:</p>
                <div style={{
                  fontSize: '28px',
                  fontWeight: '900',
                  letterSpacing: '0.2em',
                  color: '#10b981',
                  background: 'rgba(15,23,42,0.6)',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid rgba(59,130,246,0.3)',
                  display: 'inline-block',
                  width: '100%',
                  boxSizing: 'border-box'
                }}>
                  {walletOtpData.otp}
                </div>
                <p style={{ fontSize: '10px', color: '#93c5fd', marginTop: '8px', fontFamily: 'monospace' }}>
                  Expires at: {new Date(walletOtpData.expiresAt).toLocaleTimeString()}
                </p>
              </div>
            </section>
          )}

          {/* Ride route summary */}
          <section className="lt-card lt-route-card">
            <h2 className="lt-card-title">Trip Route</h2>

            <div className="lt-route-row">
              <span className="lt-route-icon lt-route-pickup"><LocationPinIcon color="#10b981" /></span>
              <div>
                <p className="lt-route-label">Pickup</p>
                <p className="lt-route-addr">{ride.pickup.address || "—"}</p>
              </div>
            </div>

            {(ride.stops || []).map((s, i) => (
              <div className="lt-route-row" key={i}>
                <span className="lt-route-icon lt-route-stop"><LocationPinIcon color="#f59e0b" /></span>
                <div>
                  <p className="lt-route-label">Stop {i + 1}</p>
                  <p className="lt-route-addr">{s.address || "—"}</p>
                </div>
              </div>
            ))}

            <div className="lt-route-row">
              <span className="lt-route-icon lt-route-drop"><FlagIcon /></span>
              <div>
                <p className="lt-route-label">Drop</p>
                <p className="lt-route-addr">{ride.drop.address || "—"}</p>
              </div>
            </div>
          </section>

          {/* Driver live coordinates */}
          {liveCoords && (
            <section className="lt-card lt-coords-card">
              <h2 className="lt-card-title">🚗 Driver Position</h2>
              <div className="lt-coords-grid">
                <div className="lt-coord-item">
                  <span className="lt-coord-label">Latitude</span>
                  <span className="lt-coord-value">{liveCoords.lat.toFixed(6)}</span>
                </div>
                <div className="lt-coord-item">
                  <span className="lt-coord-label">Longitude</span>
                  <span className="lt-coord-value">{liveCoords.lng.toFixed(6)}</span>
                </div>
              </div>
            </section>
          )}

          {/* Passenger live coordinates */}
          {passengerCoords && (
            <section className="lt-card lt-coords-card">
              <h2 className="lt-card-title">🧑 Passenger Position</h2>
              <div className="lt-coords-grid">
                <div className="lt-coord-item">
                  <span className="lt-coord-label">Latitude</span>
                  <span className="lt-coord-value">{passengerCoords.lat.toFixed(6)}</span>
                </div>
                <div className="lt-coord-item">
                  <span className="lt-coord-label">Longitude</span>
                  <span className="lt-coord-value">{passengerCoords.lng.toFixed(6)}</span>
                </div>
              </div>
            </section>
          )}

          {/* Driver info */}
          {ride.driver && (
            <section className="lt-card lt-driver-card">
              <h2 className="lt-card-title">Driver</h2>
              <div className="lt-driver-info">
                <div className="lt-driver-avatar">
                  {ride.driver.profileImage ? (
                    <img src={ride.driver.profileImage} alt={ride.driver.name} />
                  ) : (
                    <span>🧑</span>
                  )}
                </div>
                <div className="lt-driver-details">
                  <p className="lt-driver-name">{ride.driver.name}</p>
                  {ride.driver.vehicleNumber && (
                    <p className="lt-driver-vehicle">
                      <CarIcon /> {ride.driver.vehicleNumber}
                    </p>
                  )}
                  {ride.driver.mobile && (
                    <a href={`tel:${ride.driver.mobile}`} className="lt-driver-call">
                      <PhoneIcon /> {ride.driver.mobile}
                    </a>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Passenger info */}
          {ride.passenger && (
            <section className="lt-card lt-driver-card">
              <h2 className="lt-card-title">Passenger</h2>
              <div className="lt-driver-info">
                <div className="lt-driver-avatar">
                  <span>🧑</span>
                </div>
                <div className="lt-driver-details">
                  <p className="lt-driver-name">{ride.passenger.name}</p>
                </div>
              </div>
            </section>
          )}

          {/* Cab info */}
          {ride.cab && (
            <section className="lt-card lt-cab-card">
              <h2 className="lt-card-title">Vehicle</h2>
              <div className="lt-cab-info">
                {ride.cab.imageUrl && (
                  <img className="lt-cab-img" src={ride.cab.imageUrl} alt={ride.cab.name} />
                )}
                <div>
                  <p className="lt-cab-name">{ride.cab.name}</p>
                  <p className="lt-cab-meta">
                    {ride.cab.seatingCapacity && <span>👥 {ride.cab.seatingCapacity} seats</span>}
                    {ride.cab.acType && <span>❄️ {ride.cab.acType}</span>}
                  </p>
                </div>
              </div>
            </section>
          )}

          {/* Share link */}
          <section className="lt-card lt-share-card">
            <h2 className="lt-card-title">Share Live Tracking</h2>
            <div className="lt-share-url">{window.location.href}</div>
            <button
              id="lt-copy-btn"
              className="lt-copy-btn"
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                document.getElementById("lt-copy-btn").textContent = "✅ Copied!";
                setTimeout(() => {
                  const btn = document.getElementById("lt-copy-btn");
                  if (btn) btn.textContent = "📋 Copy Link";
                }, 2000);
              }}
            >
              📋 Copy Link
            </button>
          </section>
        </aside>
      </div>
    </div>
  );
}

/* ─── Sub-components ───────────────────────────────────────────────────────── */
function TrackingLoader() {
  return (
    <div className="lt-loader">
      <div className="lt-loader-car">🚖</div>
      <p className="lt-loader-text">Loading live tracking…</p>
      <div className="lt-loader-bar"><div className="lt-loader-bar-inner" /></div>
    </div>
  );
}

function TrackingError({ message, rideId }) {
  return (
    <div className="lt-error-wrap">
      <div className="lt-error-card">
        <div className="lt-error-icon">🗺️</div>
        <h2 className="lt-error-title">Ride Not Found</h2>
        <p className="lt-error-msg">{message}</p>
        <p className="lt-error-sub">Ride ID: <strong>{rideId}</strong></p>
        <a href="/" className="lt-error-btn">Go Home</a>
      </div>
    </div>
  );
}
